import React, { ChangeEvent, useCallback, useEffect, useMemo, useReducer, useState } from 'react';

import { arrayMove } from '@dnd-kit/sortable';
import sortBy from 'lodash/sortBy';
import { fetchTagsReducer, initialTagsState } from '~/reducers/marketing';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { Experiences } from '@luxuryescapes/contract-svc-experience';
import { getRegionNamesAndCode } from '@luxuryescapes/lib-regions';

import PageSubheader from '~/components/Common/Elements/PageSubheader';
import { Badge, Spinner } from '~/components/Experiences/components';
import { getPreviewURL } from '~/components/Experiences/helpers';
import { useUpdateExperienceValues } from '~/components/Experiences/hooks';

import {
  DEAL_DESCRIPTION,
  EXP_PROVIDER_ASPIRE,
  EXP_PROVIDER_DERBYSOFT,
  EXP_PROVIDER_KLOOK,
  EXP_PROVIDER_LED,
  EXP_PROVIDER_REZDY,
  IMAGES,
  INCLUDED,
  ORIGINAL_EXPERIENCE_CONTENT_TAB,
  SHORT_DESCRIPTION,
  THINGS_TO_KNOW,
  TITLE,
  curationCommentOptions,
  getStatusByCurated,
} from '~/consts/experiences';
import { TAG_TYPE_CAMPAIGN } from '~/consts/tags';

import {
  CurateExperienceParams,
  CurationStatus,
  ExperienceOffer,
  deleteCollectionOfferLink,
} from '~/services/ExperiencesService';
import offersService from '~/services/OffersService';

import ExperienceCurationImages from './ExperienceCurationImages';
import ExperienceCurationOverview from './ExperienceCurationOverview';
import ExperienceOfferContentCopyModal, { EditableOfferDetails } from './ExperienceOfferContentCopyModal';
import ExperienceOfferVendorUpdatedWarningModal from './ExperienceOfferVendorUpdatedWarningModal';

// Temporary type extension until contract is published with curationComment field
type ExperienceOfferWithCurationComment = ExperienceOffer & {
  curationComment?: string;
};

interface Props {
  experience: ExperienceOfferWithCurationComment;
  loading: boolean;
  tenant: App.Tenant;
  createExperience: ({ curationStatus }: CurateExperienceParams) => Promise<void>;
}

type CurationStatusProps = {
  label: string;
  value: CurationStatus | undefined;
};

const SELECT_ALL_OPTION = 'All';

export default function ExperienceCurationOptions({ experience, createExperience, tenant }: Props) {
  const [images, setImages] = useState<Array<Experiences.Image>>([]);
  const [imageHero, setImageHero] = useState<string | number | null>(null);
  const [imagesToRemove, setImagesToRemove] = useState<Array<string | number>>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<Array<string>>([]);
  const [selectedExcludedRegions, setSelectedExcludedRegions] = useState<Array<string>>([]);
  const [selectedCollections, setSelectedCollections] = useState<Array<{ id: string; theme: string }>>([]);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [vendorInfoChangedModalOpen, setVendorInfoChangedModalOpen] = useState(false);
  const [editMultiSelect, setEditMultiSelect] = useState({
    campaigns: false,
    regions: false,
  });
  const [collectionsError, setCollectionsError] = useState<string | null>(null);
  const [curationStatusValue, setCurationStatus] = useState<CurationStatusProps>({
    label: 'Not Curated',
    value: 'NOT_CURATED',
  });
  const [showCommissionMarginField, setShowCommissionMarginField] = useState<boolean>(false);

  const { updateValues, values, payload, contentTab } = useUpdateExperienceValues();

  const areOriginalValues = contentTab === ORIGINAL_EXPERIENCE_CONTENT_TAB;

  const statusLabel = useMemo(
    () => ({
      APPROVED: 'Approved',
      REJECTED: 'Rejected',
      NOT_CURATED: 'Not Curated',
    }),
    [],
  );

  useEffect(() => {
    if (experience) {
      const { campaigns = [], excludedRegions = [], images = [], collections = [] } = experience;

      if (campaigns.length) {
        setSelectedCampaigns(campaigns);
        setEditMultiSelect((prev) => ({ ...prev, campaigns: true }));
      }

      if (excludedRegions.length) {
        setSelectedExcludedRegions(excludedRegions.map((region) => region.regionCode));
        setEditMultiSelect((prev) => ({ ...prev, regions: true }));
      }

      if (collections.length) {
        setSelectedCollections(collections);
      }

      if (experience?.curationStatus) {
        setCurationStatus({
          label: statusLabel[experience.curationStatus],
          value: experience.curationStatus,
        });
      }

      if (images.length) {
        setImages(images);
      }

      if (experience.commissionMargin) {
        setShowCommissionMarginField(true);
      }
    }
  }, [experience, payload?.categories, statusLabel]);

  const [campaigns, campaignsDispatch] = useReducer(fetchTagsReducer, initialTagsState);

  const fetchCampaigns = async (): Promise<void> => {
    campaignsDispatch({ type: 'LOAD' });
    try {
      const res = await offersService.getTags(TAG_TYPE_CAMPAIGN);
      campaignsDispatch({
        type: 'FETCH',
        payload: sortBy(res.result, ['name']),
      });
    } catch (error) {
      campaignsDispatch({ type: 'ERROR', message: error.message });
    }
  };

  useEffect(() => {
    if (!areOriginalValues) {
      fetchCampaigns();
    }
  }, [areOriginalValues]);

  const handleDeleteCollection = async (collectionId: string): Promise<void> => {
    try {
      await deleteCollectionOfferLink({ collectionId, offerId: experience.id });
      setSelectedCollections((prev) => prev.filter((col) => col.id !== collectionId));
    } catch (error) {
      setCollectionsError(error.message || 'Failed to delete collection link');
    }
  };

  const handleSelectedCampaigns = (campaigns) => {
    setSelectedCampaigns(campaigns);
    updateValues({ campaigns });
  };

  const handleSelectedExcludedRegions = (excludedRegions) => {
    setSelectedExcludedRegions(excludedRegions);
    updateValues({
      excludedRegions: excludedRegions.map((region) => ({
        regionCode: region,
      })),
    });
  };

  const campaignsOptions = useMemo(
    () =>
      campaigns.tags.map((campaign) => ({
        label: campaign.name,
        value: campaign.name,
      })),
    [campaigns.tags],
  );

  const excludedRegionOptions = useMemo(
    () =>
      getRegionNamesAndCode(tenant.brand).map((region) => ({
        label: region.name,
        value: region.code,
      })),
    [tenant.brand],
  );

  const curationStatus = [
    { label: 'Approved', value: 'APPROVED' },
    { label: 'Rejected', value: 'REJECTED' },
    { label: 'Not Curated', value: 'NOT_CURATED' },
  ];

  const status = getStatusByCurated(experience?.curationStatus);

  const handleSetImageHero = (imageId: number | string) => {
    setImageHero(imageId);
    updateValues({ heroImageId: imageId?.toString() });
  };

  const handleVendorBookingCutoffChange = (event) => {
    const value = parseInt(event.target.value || 0);
    updateValues({ vendorBookingCutoff: value });
  };

  const handleMarginChange = (event) => {
    const value = event.target.value ? parseInt(event.target.value) : null;
    updateValues({ margin: value });
  };

  const handleCommissionMarginChange = (event) => {
    const value = event.target.value ? parseInt(event.target.value) : null;
    updateValues({ commissionMargin: value });
  };

  const handleShowCommissionMarginField = (event) => {
    setShowCommissionMarginField(event.target.checked);
    if (!event.target.checked) {
      updateValues({ commissionMargin: null });
    }
  };

  const handleBookingCancellationCutoffChange = (event) => {
    const value = parseInt(event.target.value || 0);
    const cancellationInfo = payload?.cancellationInfo ?? experience?.cancellationInfo;
    cancellationInfo.daysCutoff = value;
    updateValues({ cancellationInfo });
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) =>
    updateValues({ imagesToUpload: Array.from(event.target.files) });

  const handleDeleteImage = (imageId: number | string) => {
    let updatedImagesToRemove = [];
    const includes = imagesToRemove.includes(imageId);

    if (includes) {
      updatedImagesToRemove = imagesToRemove.filter((imageToRemove) => imageToRemove !== imageId);
    } else {
      updatedImagesToRemove = [...imagesToRemove, imageId];
    }

    updateValues({ imagesToRemove: updatedImagesToRemove });
    setImagesToRemove(updatedImagesToRemove);
  };

  const onDragEnd = ({ active, over }) => {
    if (!active?.id || !over?.id) {
      return;
    }

    const oldIndex = images.findIndex((image) => image.id === active.id);
    const newIndex = images.findIndex((image) => image.id === over.id);

    const movedImages = arrayMove(images, oldIndex, newIndex);
    setImages(movedImages);
    updateValues({ images: movedImages });
  };

  const isRezdy = experience?.provider === EXP_PROVIDER_REZDY;
  const isLED = experience?.provider === EXP_PROVIDER_LED;
  const isDerbysoft = experience?.provider === EXP_PROVIDER_DERBYSOFT;
  const isKlook = experience?.provider === EXP_PROVIDER_KLOOK;
  const isAspire = experience?.provider === EXP_PROVIDER_ASPIRE;

  const enableImagesUpload = isRezdy || isDerbysoft || isKlook || isAspire;

  const transferEnabled = !!values?.features?.transfer;

  const hasPriceOverridden = values?.tickets?.some((ticket) => ticket.margin);

  const handleOverriddenFields = (isOverwriting: boolean, key: string) => {
    const index = values.overriddenFields.indexOf(key);
    if (isOverwriting && index < 0) {
      values.overriddenFields.push(key);
    } else if (!isOverwriting && index >= 0) {
      values.overriddenFields.splice(index, 1);
    }
    updateValues({ overriddenFields: values.overriddenFields });
  };

  const updateValuesFromCopyOffer = useCallback(
    (editableOfferDetails: EditableOfferDetails, selectedCopyOptions: Array<string>) => {
      if (selectedCopyOptions.includes(TITLE)) {
        updateValues({ title: editableOfferDetails.title });
      }

      if (selectedCopyOptions.includes(IMAGES)) {
        updateValues({ images: editableOfferDetails.images });
        setImages(editableOfferDetails.images);
      }

      if (selectedCopyOptions.includes(DEAL_DESCRIPTION)) {
        updateValues({ dealDescription: editableOfferDetails.dealDescription });
      }

      if (selectedCopyOptions.includes(SHORT_DESCRIPTION)) {
        updateValues({ shortDescription: editableOfferDetails.shortDescription });
      }

      if (selectedCopyOptions.includes(INCLUDED)) {
        updateValues({ included: editableOfferDetails.included });
      }

      if (selectedCopyOptions.includes(THINGS_TO_KNOW)) {
        updateValues({ thingsToKnowNew: editableOfferDetails.thingsToKnowNew });
      }
    },
    [updateValues],
  );

  return (
    <Stack direction="column" spacing={2}>
      <PageSubheader title={experience?.title}>
        <Badge title={status?.title} background={status?.color} />
      </PageSubheader>

      <Box display="flex" gap={2}>
        <Button
          variant="outlined"
          onClick={() => setCopyModalOpen(true)}
          startIcon={<ContentCopyIcon />}
          aria-label="Copy Content From Offer"
        >
          Copy Content From Offer
        </Button>
      </Box>

      <Box maxWidth="40%" display="grid" gap={2} alignItems="center" gridTemplateColumns="repeat(3, 1fr)">
        <Typography fontWeight="bold" fontSize={16}>
          Curation Status:
        </Typography>
        <Box gridColumn="span 2">
          <FormControl fullWidth>
            <InputLabel id="curation-status-label">Curation status</InputLabel>
            <Select
              labelId="curation-status-label"
              id="curation-status"
              label="Curation status"
              value={curationStatusValue.value}
              onChange={(e) => {
                const value = e.target.value;

                setCurationStatus({
                  label: curationStatus.find((status) => status.value === value).label,
                  value,
                });
                updateValues({ curationStatus: value });
              }}
            >
              {curationStatus.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {!isLED && (
          <>
            <Typography fontWeight="bold" fontSize={16}>
              Curation Comment:
            </Typography>
            <Box gridColumn="span 2">
              <FormControl fullWidth>
                <InputLabel id="curation-comment-label">Curation comment</InputLabel>
                <Select
                  labelId="curation-comment-label"
                  id="curation-comment"
                  label="Curation comment"
                  value={(values as ExperienceOfferWithCurationComment)?.curationComment || ''}
                  onChange={(e) => {
                    updateValues({ curationComment: e.target.value });
                  }}
                  disabled={areOriginalValues}
                >
                  {curationCommentOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </>
        )}

        <Typography fontWeight="bold" fontSize={16}>
          Preview:
        </Typography>
        <Box gridColumn="span 2">
          <Button variant="outlined" href={getPreviewURL(tenant.value, experience?.id)} target="_blank">
            {tenant.title}
          </Button>
        </Box>
      </Box>

      <Box>
        <Button variant="outlined" onClick={() => window.close()}>
          Cancel
        </Button>
      </Box>

      <Box>
        <Typography fontWeight="bold" fontSize={16}>
          Images:
        </Typography>

        <Box>
          <ExperienceCurationImages
            items={images}
            imagesToRemove={imagesToRemove}
            imageHero={imageHero}
            onDragEnd={onDragEnd}
            handleSetImageHero={handleSetImageHero}
            handleDeleteImage={handleDeleteImage}
          />
          {enableImagesUpload && (
            <Box>
              <Typography fontWeight="bold" fontSize={16}>
                Upload new images:
              </Typography>

              <TextField
                inputProps={{ accept: 'image/*', multiple: true }}
                type="file"
                onChange={onFileChange}
                disabled={areOriginalValues}
              />
            </Box>
          )}
        </Box>
      </Box>

      <Stack direction="row" spacing={2} justifyContent="flex-start" alignItems="center">
        <Typography fontWeight="bold" fontSize={16}>
          Collections:
        </Typography>

        {collectionsError && <Alert severity="error">{collectionsError}</Alert>}

        <Box flexGrow={1}>
          {selectedCollections.length > 0 ? (
            <Box display="flex" flexWrap="wrap" gap={1}>
              {selectedCollections.map((collection) => (
                <Chip
                  key={collection.id}
                  label={collection.theme}
                  onDelete={!areOriginalValues ? () => handleDeleteCollection(collection.id) : undefined}
                  deleteIcon={
                    !areOriginalValues ? (
                      <IconButton size="small" aria-label="delete collection">
                        <DeleteIcon />
                      </IconButton>
                    ) : undefined
                  }
                  disabled={areOriginalValues}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No collections assigned
            </Typography>
          )}
        </Box>
      </Stack>

      <Stack direction="row" spacing={2} justifyContent="flex-start" alignItems="center">
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={editMultiSelect.campaigns}
                onChange={(e) =>
                  setEditMultiSelect({
                    ...editMultiSelect,
                    campaigns: e.target.checked,
                  })
                }
                disabled={areOriginalValues}
              />
            }
            label="Add campaign tag:"
          />
        </FormGroup>

        {campaigns.loading && <Spinner size={25} />}
        {campaigns.error && <Alert severity="error">{campaigns.message}</Alert>}

        {!campaigns.loading && !campaigns.error && (
          <Box flexGrow={1}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              disabled={!editMultiSelect.campaigns}
              options={[SELECT_ALL_OPTION, ...campaignsOptions.map((option) => option.value)]}
              limitTags={7}
              onChange={(_, values) => {
                if (values.filter((item) => item === SELECT_ALL_OPTION).length === 1) {
                  handleSelectedCampaigns(campaignsOptions);
                } else {
                  handleSelectedCampaigns(values);
                }
              }}
              value={selectedCampaigns}
              renderInput={(params) => <TextField {...params} label="Select tags" />}
            />
          </Box>
        )}
      </Stack>

      <Stack direction="row" spacing={2} justifyContent="flex-start" alignItems="center ">
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={editMultiSelect.regions}
                onChange={(e) =>
                  setEditMultiSelect({
                    ...editMultiSelect,
                    regions: e.target.checked,
                  })
                }
                disabled={areOriginalValues}
              />
            }
            label="Add excluded region:"
          />
        </FormGroup>

        <Box flexGrow={1}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            disabled={!editMultiSelect.regions}
            options={[SELECT_ALL_OPTION, ...excludedRegionOptions.map((option) => option.value)]}
            limitTags={7}
            onChange={(_, values) => {
              if (values.filter((item) => item === SELECT_ALL_OPTION).length === 1) {
                handleSelectedExcludedRegions(excludedRegionOptions.map((option) => option.value));
              } else {
                handleSelectedExcludedRegions(values);
              }
            }}
            value={selectedExcludedRegions}
            renderInput={(params) => <TextField {...params} label="Select regions" />}
            getOptionLabel={(option) =>
              excludedRegionOptions.find((region) => region.value === option)?.label || option
            }
          />
        </Box>
      </Stack>

      <Stack direction="row" spacing={2} justifyContent="flex-start" alignItems="center">
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={values?.overriddenFields?.includes('ticketUnitLabel') ?? false}
                onChange={(e) => handleOverriddenFields(e.target.checked, 'ticketUnitLabel')}
                disabled={areOriginalValues}
              />
            }
            label="Override ticket unit label:"
          />
        </FormGroup>

        <Box flexGrow={1}>
          <TextField
            onChange={(e) => updateValues({ ticketUnitLabel: e.target.value })}
            value={values?.ticketUnitLabel ?? ''}
            disabled={areOriginalValues || !values?.overriddenFields?.includes('ticketUnitLabel')}
          />
        </Box>
      </Stack>

      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={values?.hideTimeSlots ?? false}
              onChange={(e) => updateValues({ hideTimeSlots: e.target.checked })}
              disabled={areOriginalValues}
            />
          }
          label="Hide time slots"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={values?.isNonRefundable ?? false}
              disabled={areOriginalValues}
              onChange={(e) => updateValues({ isNonRefundable: e.target.checked })}
            />
          }
          label="Non-refundable"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={values?.allowBuyNowBookLater ?? false}
              onChange={(e) => updateValues({ allowBuyNowBookLater: e.target.checked })}
              disabled={areOriginalValues || transferEnabled || isRezdy || isDerbysoft || isKlook || isAspire}
            />
          }
          label={
            transferEnabled
              ? 'Allow Buy Now Book Later / Gifting (You cannot enable it if you have allowed Transfer)'
              : 'Allow Buy Now Book Later / Gifting'
          }
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={values?.allowGiftCard ?? true}
              onChange={(e) => updateValues({ allowGiftCard: e.target.checked })}
              disabled={areOriginalValues}
            />
          }
          label="Allow Gift Card"
        />

        {transferEnabled && (
          <FormControlLabel
            control={
              <Checkbox
                checked={!!values?.features?.transfer?.preferredTransfer}
                onChange={(e) =>
                  updateValues({
                    features: {
                      ...values.features,
                      transfer: { ...values.features.transfer, preferredTransfer: e.target.checked },
                    },
                  })
                }
              />
            }
            label="Preferred transfer (one per region)"
          />
        )}

        {isLED && (
          <FormControlLabel
            control={
              <Checkbox
                checked={values?.ticketed ?? false}
                disabled={areOriginalValues}
                onChange={(e) => updateValues({ ticketed: e.target.checked })}
              />
            }
            label="Ticketed Experience"
          />
        )}

        <FormControlLabel control={<Checkbox checked={hasPriceOverridden} disabled />} label="Price Overridden" />
      </FormGroup>

      <Box>
        <TextField
          label="Booking Cancellation Cutoff (Amount in days)"
          type="number"
          onChange={handleBookingCancellationCutoffChange}
          value={payload?.cancellationInfo?.daysCutoff || experience?.cancellationInfo?.daysCutoff || 0}
          required
          fullWidth
        />
      </Box>

      <Box>
        <TextField
          label="Vendor Booking Cutoff (Amount in hours)"
          type="number"
          onChange={handleVendorBookingCutoffChange}
          value={payload?.vendorBookingCutoff || experience?.vendorBookingCutoff || 0}
          required
          fullWidth
        />
      </Box>

      <Box>
        <TextField
          label="Profit Margin %"
          name="margin"
          onChange={handleMarginChange}
          value={values?.margin ?? 0}
          disabled={areOriginalValues}
          type="number"
          inputProps={{ max: 100 }}
          helperText="Profit margin calculated based on the sales price. This margin affects the sales price of the deal."
          fullWidth
        />
      </Box>

      {!!isRezdy && (
        <>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showCommissionMarginField}
                  disabled={areOriginalValues}
                  onChange={handleShowCommissionMarginField}
                />
              }
              label="This supplier does not provide cost price via Rezdy"
            />
          </FormGroup>

          {showCommissionMarginField && (
            <TextField
              name="commission-margin"
              onChange={handleCommissionMarginChange}
              label="Commission Margin %"
              value={values?.commissionMargin ?? 0}
              disabled={areOriginalValues}
              type="number"
              inputProps={{ max: 100 }}
              helperText="Equivalent to the percentage that will be deducted from the vendor's price as a commission for Luxury Escapes. This margin does not modify the deal's selling price."
              fullWidth
            />
          )}
        </>
      )}

      <ExperienceCurationOverview />

      <Box>
        <Typography fontWeight="bold" fontSize={16} mb={1}>
          Video
        </Typography>
        <Typography>Insert the Vimeo video ID</Typography>

        <Typography color="secondary">
          The ID is located at:{' '}
          <i>
            http://vimeo.com/
            <strong>1234567890</strong>
          </i>
        </Typography>

        <TextField
          onChange={(e) => updateValues({ video: e.target.value })}
          placeholder="Ex.: 1234567890"
          value={values?.video || ''}
          disabled={areOriginalValues}
        />
      </Box>
      <ExperienceOfferContentCopyModal
        isCopyModalVisible={copyModalOpen}
        hideCopyModal={() => setCopyModalOpen(false)}
        originalOfferDetails={experience}
        copyOfferData={updateValuesFromCopyOffer}
      />
      <ExperienceOfferVendorUpdatedWarningModal
        isModalVisible={vendorInfoChangedModalOpen}
        hideModal={() => setVendorInfoChangedModalOpen(false)}
        saveOperation={() => createExperience({ curationStatus: values?.curationStatus })}
      />
    </Stack>
  );
}
